import {
  NEEDS_YOU_FEED_SECTION,
  WORKING_FEED_GROUP,
  type ActionCard,
} from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useState, type ReactNode } from 'react';

import { YStack } from '@/components/ui';
import {
  useDelegatedSourceCardIds,
  useDelegatedSummary,
} from '@/features/action-card-delegation';
import { FeedDensityContext } from '@/features/action-cards-view/feed/feedDensity';
import {
  ConnectAppsFeedCard,
  useShowsConnectAppsCard,
} from '@/features/feed/components/ConnectAppsFeedCard';
import {
  FeedReadyChatRows,
  FeedWorkingChatRows,
} from '@/features/feed/components/FeedChats';
import type { FeedGroupLeads } from '@/features/feed/feed-groups';
import { useFeedChats } from '@/features/feed/use-feed-chats';
import { useFeedComposerEnabled } from '@/features/feed/use-feed-composer-enabled';
import { showFeedSectionInFull } from '@/features/feed/use-feed-sections';
import { useTabs } from '@/features/tabs';

import { DelegatedList } from './components/DelegatedList';
import { FeedDelegatedButton } from './components/FeedDelegatedButton';
import { FeedSection } from './components/FeedSection';
import { HomeNeedYouCards } from './components/HomeNeedYouCards';
import {
  feedNeedYouLabel,
  HOME_FEED_DENSITY,
  HOME_SECTION_ROWS,
} from './need-you-cards';
import { useHomeFeed } from './use-home-feed';
import { useWaitingForYou } from './use-waiting-for-you';

// The Feed's main tab, where Home's "Show all" lands
const FOR_YOU_TAB_ID = 'for_you';

type WaitingForYouSectionProps = {
  readonly acceptsCard?: (card: ActionCard) => boolean;
  readonly emptyMessage?: string;
  readonly footer?: ReactNode;
};

export const WaitingForYouSection = observer(
  ({ acceptsCard, emptyMessage, footer }: WaitingForYouSectionProps) => {
    // A card the user delegated lives on as the teammate's copy, so it leaves this list
    const delegatedSourceCardIds = useDelegatedSourceCardIds();
    const state = useWaitingForYou({
      acceptsCard: (card) =>
        !delegatedSourceCardIds.has(card.id) && (acceptsCard?.(card) ?? true),
      animatesRowTransitions: true,
    });
    const { openTab } = useTabs();
    const delegated = useDelegatedSummary();
    const feedComposerEnabled = useFeedComposerEnabled();
    const feedChatViews = useFeedChats(undefined, feedComposerEnabled);
    const showsConnectAppsCard = useShowsConnectAppsCard();
    const [delegatedOpen, setDelegatedOpen] = useState(false);
    const showsDelegated = delegated.openCount > 0 || delegatedOpen;
    // The Feed composer's chats lead their sections, as on the Feed
    const working = feedComposerEnabled ? feedChatViews.working : [];
    const ready = feedComposerEnabled ? feedChatViews.ready : [];
    const leads: FeedGroupLeads = {
      ...(working.length > 0
        ? {
            [WORKING_FEED_GROUP]: {
              count: working.length,
              rows: (
                <FeedWorkingChatRows
                  slot="home"
                  working={working.slice(0, HOME_SECTION_ROWS)}
                />
              ),
            },
          }
        : {}),
      ...(ready.length > 0
        ? {
            [NEEDS_YOU_FEED_SECTION]: {
              count: ready.length,
              rows: (
                <FeedReadyChatRows
                  slot="home"
                  ready={ready.slice(0, HOME_SECTION_ROWS)}
                />
              ),
            },
          }
        : {}),
    };
    const feed = useHomeFeed(state, leads);
    const hasCards = feed.layout.items.length > 0;
    const hasRows = hasCards || feed.layout.trailingHeadings.length > 0;

    return (
      <FeedDensityContext.Provider value={HOME_FEED_DENSITY}>
        <FeedSection
          // Each section says how many it holds and offers the rest, so the header names no total its rows fall
          // short of; it still says when nothing needs the user
          count={feed.needYouCount === 0 ? feedNeedYouLabel(0) : undefined}
          onOpenFeed={() => openTab({ path: '/feed' }, { reuseExisting: true })}
          actions={
            showsDelegated ? (
              <FeedDelegatedButton
                count={delegated.openCount}
                open={delegatedOpen}
                onToggle={() => setDelegatedOpen((open) => !open)}
              />
            ) : null
          }
        >
          {hasRows ? (
            <YStack className="no-record">
              <HomeNeedYouCards
                home={state}
                feed={feed}
                leads={leads}
                onShowAll={(sectionId) => {
                  showFeedSectionInFull(FOR_YOU_TAB_ID, sectionId);
                  openTab({ path: '/feed' }, { reuseExisting: true });
                }}
              />
            </YStack>
          ) : null}
          {hasCards ? null : showsConnectAppsCard ? (
            <ConnectAppsFeedCard />
          ) : !hasRows && emptyMessage ? (
            <span className="py-1 text-xs text-foreground-subtle">
              {emptyMessage}
            </span>
          ) : null}
          {delegatedOpen ? <DelegatedList home={state} /> : null}
          {footer}
        </FeedSection>
      </FeedDensityContext.Provider>
    );
  }
);
